import type {
  CompoundPriceSpecification,
  HotelPriceSchema,
  HotelPriceWithRoomSchema,
  HotelRoomSchema,
  LodgingOffer,
  PostalAddress,
  QuantitativeValue,
  StructuredDataHotel,
  UnitPriceSpecification
} from './types'
import type {OfferPrice} from '@vio/core/src/offer/types/offer'
import type {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import type {Room} from '@vio/core/src/room/types/room'

export const buildPostalAddress = (
  hotel: StructuredDataHotel
): PostalAddress => {
  const {navPathInfo = {}} = hotel
  const {city, country, adminDivision} = navPathInfo

  return {
    '@type': 'PostalAddress',
    streetAddress: hotel.displayAddress,
    addressLocality: city?.name,
    addressRegion: adminDivision?.name,
    addressCountry: country?.name
  }
}

export const formatDateTime = (date: string, time?: string): string => {
  if (!time) return date
  return `${date} ${time}:00`
}

export const buildPriceComponentsFromOfferPrice = (
  offerPrice: OfferPrice,
  currencyCode: string
): UnitPriceSpecification[] => {
  const components: UnitPriceSpecification[] = []
  const {chargeable, hotelFees} = offerPrice

  if (chargeable?.base !== undefined) {
    components.push({
      '@type': 'UnitPriceSpecification',
      name: '',
      price: chargeable.base,
      priceCurrency: currencyCode
    })
  }

  if (chargeable?.taxes !== undefined) {
    components.push({
      '@type': 'UnitPriceSpecification',
      name: 'GenericTax',
      price: chargeable.taxes,
      priceCurrency: currencyCode,
      priceComponentType: 'GenericTax'
    })
  }

  if (hotelFees?.total !== undefined) {
    components.push({
      '@type': 'UnitPriceSpecification',
      name: 'ResortFee',
      price: hotelFees.total,
      priceCurrency: currencyCode,
      priceComponentType: 'ResortFee'
    })
  }

  return components
}

export const buildPriceSpecificationFromOfferPrice = (
  offerPrice: OfferPrice,
  currencyCode: string
): CompoundPriceSpecification => {
  const total =
    (offerPrice.chargeable?.base ?? 0) +
    (offerPrice.chargeable?.taxes ?? 0) +
    (offerPrice.hotelFees?.total ?? 0)

  return {
    '@type': 'CompoundPriceSpecification',
    price: total,
    priceCurrency: currencyCode,
    priceComponent: buildPriceComponentsFromOfferPrice(offerPrice, currencyCode)
  }
}

export const buildOccupancy = (
  occupationPerRoom: number
): QuantitativeValue => ({
  '@type': 'QuantitativeValue',
  value: occupationPerRoom
})

export interface BuildLodgingOfferParams {
  priceSpecification?: CompoundPriceSpecification
  checkinTime: string
  checkoutTime: string
  isSoldOut?: boolean
}

export const buildLodgingOffer = ({
  priceSpecification,
  checkinTime,
  checkoutTime,
  isSoldOut = false
}: BuildLodgingOfferParams): LodgingOffer => ({
  '@type': ['Offer', 'LodgingReservation'],
  checkinTime,
  checkoutTime,
  ...(isSoldOut
    ? {availability: 'https://schema.org/SoldOut'}
    : priceSpecification
      ? {priceSpecification}
      : {})
})

export const buildHotelRoom = (
  room: Pick<Room, 'id' | 'occupationPerRoom'> & {name?: string},
  offers: LodgingOffer
): HotelRoomSchema => ({
  '@type': ['HotelRoom', 'Product'],
  ...(room.name && {name: room.name}),
  ...(room.id && {identifier: room.id}),
  ...(room.occupationPerRoom && {
    occupancy: buildOccupancy(room.occupationPerRoom)
  }),
  offers
})

export interface BuildHotelRoomPriceSchemaParams {
  hotel: StructuredDataHotel
  room: Pick<Room, 'id' | 'occupationPerRoom'> & {name?: string}
  offerPrice?: OfferPrice
  checkIn: string
  checkOut: string
  currencyCode: string
  isSoldOut?: boolean
}

export const buildHotelRoomPriceSchema = ({
  hotel,
  room,
  offerPrice,
  checkIn,
  checkOut,
  currencyCode,
  isSoldOut = false
}: BuildHotelRoomPriceSchemaParams): HotelPriceWithRoomSchema => ({
  '@context': 'https://schema.org',
  '@type': 'Hotel',
  name: hotel.hotelName,
  identifier: hotel.objectID,
  address: buildPostalAddress(hotel),
  containsPlace: buildHotelRoom(
    room,
    buildLodgingOffer({
      priceSpecification:
        !isSoldOut && offerPrice
          ? buildPriceSpecificationFromOfferPrice(offerPrice, currencyCode)
          : undefined,
      checkinTime: formatDateTime(checkIn, hotel.checkInTime),
      checkoutTime: formatDateTime(checkOut, hotel.checkOutTime),
      isSoldOut
    })
  )
})

export const buildPriceComponentsFromSearchOffer = (
  offer: SearchOffer,
  currencyCode: string,
  numberOfRooms?: number
): UnitPriceSpecification[] => {
  const components: UnitPriceSpecification[] = []
  const {rate} = offer
  const rooms = numberOfRooms ?? 1

  if (rate?.base !== undefined) {
    components.push({
      '@type': 'UnitPriceSpecification',
      name: '',
      price: rate.base * rooms,
      priceCurrency: currencyCode
    })
  }

  if (rate?.taxes !== undefined) {
    components.push({
      '@type': 'UnitPriceSpecification',
      name: 'GenericTax',
      price: rate.taxes * rooms,
      priceCurrency: currencyCode,
      priceComponentType: 'GenericTax'
    })
  }

  if (rate?.hotelFees !== undefined) {
    components.push({
      '@type': 'UnitPriceSpecification',
      name: 'ResortFee',
      price: rate.hotelFees * rooms,
      priceCurrency: currencyCode,
      priceComponentType: 'ResortFee'
    })
  }
  return components
}

export const buildPriceSpecificationFromSearchOffer = (
  offer: SearchOffer,
  currencyCode: string,
  numberOfRooms?: number
): CompoundPriceSpecification => ({
  '@type': 'CompoundPriceSpecification',
  price: offer.totalRate,
  priceCurrency: currencyCode,
  priceComponent: buildPriceComponentsFromSearchOffer(
    offer,
    currencyCode,
    numberOfRooms
  )
})

export interface BuildHotelPriceSchemaParams {
  hotel: StructuredDataHotel
  offer: SearchOffer
  checkIn: string
  checkOut: string
  currencyCode: string
  isSoldOut?: boolean
  numberOfRooms?: number
}

export const buildHotelPriceSchema = ({
  hotel,
  offer,
  checkIn,
  checkOut,
  currencyCode,
  isSoldOut = false,
  numberOfRooms = 1
}: BuildHotelPriceSchemaParams): HotelPriceSchema => ({
  '@context': 'https://schema.org',
  '@type': 'Hotel',
  name: hotel.hotelName,
  identifier: hotel.objectID,
  address: buildPostalAddress(hotel),
  makesOffer: buildLodgingOffer({
    priceSpecification: isSoldOut
      ? undefined
      : buildPriceSpecificationFromSearchOffer(
          offer,
          currencyCode,
          numberOfRooms
        ),
    checkinTime: formatDateTime(checkIn, hotel.checkInTime),
    checkoutTime: formatDateTime(checkOut, hotel.checkOutTime),
    isSoldOut
  })
})
